import { State as GridState, toDataSourceRequestString } from "@progress/kendo-data-query";
import { formatNumber } from '@progress/kendo-intl';
import { Push } from "@progress/kendo-react-animation";
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { WindowActionsBar } from '@progress/kendo-react-dialogs';
import { GridColumn as Column, Grid, GridCustomCellProps, GridDetailRowProps } from '@progress/kendo-react-grid';
import { Loader } from '@progress/kendo-react-indicators';
import { Stepper, StepperChangeEvent } from '@progress/kendo-react-layout';
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import { arrowRightIcon, arrowRotateCcwIcon, checkIcon, searchIcon, warningCircleIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { JsonResponse } from '../../TypeGen/json-response';
import { PayDivision } from '../../TypeGen/pay-division';
import CenterDivPanel from '../../components/CenterDivPanel';
import KendoWindow from '../../components/KendoWindow';
import DateOnlyCell from "../../components/cells/DateOnlyCell";
import { RightAlignHeaderCell } from '../../components/cells/RightAlignHeaderCell';
import useConfirm from '../../components/useConfirm';
import { fetchApi } from '../../services/api';
import { openWindowPerc } from "../../services/openWindow";
import { IDName } from "../../types/idname";
import { PayDivisions } from "../../utils/utils";

type ReadyToPostPaySummary = {
  PayGroups: PayGroup[];
  PayPeriodGroups: PayPeriodGroup[];
}

type PayPeriodGroup = {
  Name: string;
  Division: number;
  PeriodEndDate: Date;
  PaperworkDeadline: Date;
  PayDate: Date;
  PayCount: number;
  PayPeriodID: number;
  PayTotal: number;

  Pays: Pay[];
};

type PayGroup = {
  UserID: number;
  UserName: string;
  PayCount: number;
  Pays: Pay[];
};

type Pay = {
  PayNumber: string;
  Amount: number;
  ShipDateTime: Date;
  DriverNumber: string;
  CarrierName: string;
}

interface GeneratePostBatchCommandCellProps extends GridCustomCellProps {
  generatePayPostBatch: (item: PayPeriodGroup) => void;
  openPostPayWindow: (item: PayPeriodGroup) => void;
}

const PostPayBatch = () => {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<ReadyToPostPaySummary>();
  const [message, setMessage] = useState('');
  const { ConfirmationDialog, confirm } = useConfirm({});
  const [selectedPayPeriod, setSelectedPayPeriod] = useState<PayPeriodGroup>();
  const [isPostPayWindowOpen, setIsPostPayWindowOpen] = useState(false);
  const [pendingDeductionsCount, setPendingDeductionsCount] = useState(-1);
  const [ownerDiscrepancyCount, setOwnerDiscrepancyCount] = useState(-1);


  const [step, setStep] = useState<number>(0);
  const items = [
    { label: 'Step 1', disabled: false },
    { label: 'Step 2', disabled: true },
    { label: 'Step 3', disabled: true },
  ];
  const [storedItems, setStoredItems] = useState([...items]);


  const getAnyPendingDeductionsToBeGenerated = useCallback((PayPeriodGroup: PayPeriodGroup) => {
    setLoading(true);
    setPendingDeductionsCount(-1);

    fetchApi(`/api/Settlements/GetAnyPendingDeductionsToBeGenerated/${PayPeriodGroup.PayPeriodID}`)
      .then((response: any[]) => {
        setPendingDeductionsCount(response.length);
        setLoading(false);
      })
      .catch(e => {
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404) {
          alert(e?.detail);
        }
        setLoading(false);
      });
  }, []);

  const refreshBatchDetails = useCallback(() => {
    setLoading(true);

    fetchApi(`/api/Settlements/GetReadyToPostPay`)
      .then((response: ReadyToPostPaySummary) => {
        setDetails(response);
        setLoading(false);
      })
      .catch(e => {
        if (!e?.status)
          alert('Error: Please see admin');
        else if (e.status !== 404) {
          alert(e?.detail);
        }
        setLoading(false);
      });
  }, []);

  const generatePayPostBatch = (dataItem: PayPeriodGroup) => {
    setLoading(true);
    fetchApi(`/api/Settlements/PostPayBatch`, { PayPeriodID: dataItem.PayPeriodID }, 'POST')
      .then((message: string) => {
        setMessage(message);
        refreshBatchDetails();
      })
      .catch(e => {
        if (!e?.status)
          alert('Error: Please see admin');
        setLoading(false);
      });
  }

  useEffect(() => {
    refreshBatchDetails();
  }, [refreshBatchDetails]);

  const detailComponent = (props: GridDetailRowProps) => {
    const dataItem = props.dataItem as PayGroup;
    return (
      <section style={{ maxHeight: '300px', overflow: 'auto' }}>
        <div className="card-body">
          <h5 className="card-title text-left">Trips</h5>
          <table className="table table-hover table-striped mb-0">
            <thead>
              <tr>
                <th>Pay No.</th>
                <th>Name</th>
                <th>Amount</th>
                <th>Ship Date</th>
              </tr>
            </thead>
            <tbody>
              {dataItem.Pays.map((invoice, index) => (
                <tr key={index}>
                  <td>{invoice.PayNumber}</td>
                  <td>{invoice.DriverNumber ?? invoice.CarrierName}</td>
                  <td>{formatNumber(invoice.Amount, "c")}</td>
                  <td>{dayjs.utc(invoice.ShipDateTime).tz("America/New_York").format("MM/DD/YYYY")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  };

  const RightAlignCell = (props: GridCustomCellProps) => {
    return <td {...props.tdProps} style={{ ...props.style, textAlign: "right" }}>
      {props.dataItem[props.field]}
    </td>;
  };

  const DivisionCell = (props: GridCustomCellProps) => {
    const dataItem: PayPeriodGroup = props.dataItem;

    return (
      <td {...props.tdProps}>
        <div>{dataItem.Division > 0 ? PayDivisions.find((x: IDName) => x.ID == dataItem.Division).Name : 'N/A'}</div>
      </td>
    );
  }

  const MyEditCommandCell = (props: GridCustomCellProps) => (
    <GeneratePostBatchCommandCell {...props}
      generatePayPostBatch={generatePayPostBatch}
      openPostPayWindow={openPostPayWindow}
    />
  );

  const getPPPaperworkDateDifferenceInDays = (dataItem: PayPeriodGroup): number => {
    if (dataItem.PayPeriodID > 0) {
      const today = dayjs().tz("America/New_York").endOf('day');
      const paperworkDeadlineDate = dayjs.utc(dataItem.PaperworkDeadline).tz("America/New_York");
      return today.diff(paperworkDeadlineDate, 'days');
    }
    return 1;
  }

  const getPPPayDateDifferenceInDays = (dataItem: PayPeriodGroup): number => {
    if (dataItem.PayPeriodID > 0) {
      const today = dayjs().tz("America/New_York");
      const payDate = dayjs.utc(dataItem.PayDate).tz("America/New_York");
      return payDate.diff(today, 'days');
    }
    return 0;
  }

  const getPPConfirmationInfo = (dataItem: PayPeriodGroup): string => {
    if (dataItem.PayPeriodID > 0) {
      const payDate = dayjs.utc(dataItem.PayDate).tz("America/New_York");
      const periodEndDate = dayjs.utc(dataItem.PeriodEndDate).tz("America/New_York");
      const differenceInDays = getPPPayDateDifferenceInDays(dataItem);

      return (`${PayDivision[dataItem.Division]} pay ending on ${periodEndDate.format("MM/DD/YYYY")} is set to be paid on ` +
        `${payDate.format("MM/DD/YYYY")}, which is in ${differenceInDays} day(s) from today.` +
        `\n\nWARNING: Pay date is ${differenceInDays} day(s) out.` +
        `\n\nPost/Close pay period ${dataItem.Name} for ${PayDivisions.find(x => x.ID === dataItem.Division).Name}?`);
    } else {
      return ('Post Brokered Loads?');
    }
  };

  const openPostPayWindow = (payPeriod: PayPeriodGroup) => {
    setSelectedPayPeriod(payPeriod);
    setIsPostPayWindowOpen(true);
  };

  const closePostPayWindow = () => {
    setStep(0);
    setStoredItems([...items]);
    setIsPostPayWindowOpen(false);
    setSelectedPayPeriod(undefined);
  };

  const GeneratePostBatchCommandCell = (_props: GeneratePostBatchCommandCellProps) => {

    const dataItem: PayPeriodGroup = _props.dataItem;

    return <td {..._props.tdProps} className={[_props.tdProps?.className, "k-command-cell"].filter(Boolean).join(" ")}>
      <Button themeColor={"primary"}
        disabled={getPPPaperworkDateDifferenceInDays(dataItem) < 0 || dataItem.PayCount === 0}
        onClick={async () => {
          if (dataItem.PayPeriodID > 0) {
            _props.openPostPayWindow(dataItem);
            return;
          }
          if (!await confirm(`${getPPConfirmationInfo(dataItem)}`, 'Confirm'))
            return;
          _props.generatePayPostBatch(dataItem);
        }}>
        {dataItem.PayPeriodID > 0 ? `Post ${dataItem.Name} (${dataItem.PayPeriodID}) Pay Period` : 'Post Brokered Loads'}
      </Button>
    </td>
  };

  useEffect(() => {
    if (isPostPayWindowOpen && selectedPayPeriod) {
      if (step === 0)
        getAnyPendingDeductionsToBeGenerated(selectedPayPeriod);
      else if (step === 1)
        getOwnerDiscrepancies(selectedPayPeriod);
    }
  }, [isPostPayWindowOpen, selectedPayPeriod, getAnyPendingDeductionsToBeGenerated, step]);

  const generateDeductions = (payPeriodGroup: PayPeriodGroup) => {
    setLoading(true);

    fetchApi(`api/Settlements/GenerateDeductions/${payPeriodGroup.PayPeriodID}`, {}, 'POST')
      .then(async (response: JsonResponse) => {
        if (response.Success === false) {
          setLoading(false);
          alert(`Error: ${response.ErrorMessage}`);
        }
        else {
          getAnyPendingDeductionsToBeGenerated(payPeriodGroup);
        }
      })
      .catch(async e => {
        setLoading(false);
        // If not problem details
        if (!e?.status) await alert('An error occurred while saving.');
      });
  }

  const getOwnerDiscrepancies = (payPeriodGroup: PayPeriodGroup) => {
    setLoading(true);
    setOwnerDiscrepancyCount(-1);

    const dataState = { skip: 0, take: 1 } as GridState;
    const queryStr = toDataSourceRequestString(dataState);
    Promise.all([
      fetchApi(`/api/Settlements/GetPayOwnerDiscrepancies?${queryStr}`, { PayPeriodID: payPeriodGroup.PayPeriodID }, "POST"),
      fetchApi(`/api/Settlements/GetDeductionOwnerDiscrepancies?${queryStr}`, { PayPeriodID: payPeriodGroup.PayPeriodID }, "POST")
    ])
      .then(([payResponse, deductionResponse]: any[]) => {
        const payTotal = (payResponse?.Total as number) ?? 0;
        const deductionTotal = (deductionResponse?.Total as number) ?? 0;
        setOwnerDiscrepancyCount(payTotal + deductionTotal);
      })
      .catch(async e => {
        setLoading(false);
        // If not problem details
        if (!e?.status) await alert('An error occurred.');
      })
      .finally(() => setLoading(false));
  }

  const pp = selectedPayPeriod ? `${selectedPayPeriod.Name} (${selectedPayPeriod.PayPeriodID}) (${selectedPayPeriod.Division > 0 ? PayDivisions.find((x: IDName) => x.ID === selectedPayPeriod.Division)?.Name : 'N/A'})` : '';

  return (
    <div style={{ position: "relative", height: 'auto' }} className="mt-4">
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}
      {isPostPayWindowOpen && selectedPayPeriod &&
        <KendoWindow
          title={`Post Pay Period - ${pp}`}
          onClose={closePostPayWindow}
          modal={true}
          autoSizePercentage={70}>
          {loading && <CenterDivPanel>
            <Loader type="converging-spinner" />
          </CenterDivPanel>}
          <div className="p-3">
            <div className="row">
              <div className="col-1">
              </div>
              <div className="col-10 text-center">
                <Stepper value={step} onChange={(e: StepperChangeEvent) => { setStep(e.value); }} items={storedItems} animationDuration mode={'labels'} />
                <div className="row mt-3">
                  <div className="col-2">
                  </div>
                  <div className="col-8 text-left">
                    <br />
                    <h2>{step === 0 ? 'Step 1: Pending Deductions' : step === 1 ? 'Owner Discrepancies' : 'Generate Post Batch'}</h2>
                    <br />
                    {step === 0 && <div>
                      {pendingDeductionsCount > 0 ? <h5>
                        You have {pendingDeductionsCount} deduction{pendingDeductionsCount > 1 ? 's' : ''} to be generated
                        <SvgIcon icon={warningCircleIcon} themeColor='error' className="ml-2" size="xlarge" />
                      </h5> : ''}
                      {pendingDeductionsCount > 0 && <Button onClick={() => generateDeductions(selectedPayPeriod)} themeColor='primary' disabled={loading}>Generate Pending Deductions</Button>}
                      {(pendingDeductionsCount === 0 && !loading) ?
                        <div>
                          <h5>
                            All deductions have been generated
                            <SvgIcon icon={checkIcon} themeColor='success' className="ml-2" size="xlarge" />
                          </h5>
                          <Button svgIcon={arrowRightIcon} onClick={() => { setStep(1); }} themeColor='success' disabled={loading}>Next Step</Button>
                        </div>
                        : (pendingDeductionsCount === -1 && loading) && <div>
                          <h5>
                            Checking for deductions that still need to be generated...
                            <SvgIcon icon={warningCircleIcon} themeColor='warning' className="ml-2" size="xlarge" />
                          </h5>
                        </div>
                      }
                    </div>}
                    {step === 1 && <div>
                      {ownerDiscrepancyCount > 0 && <h5>
                        You have {ownerDiscrepancyCount} potential owner discrepanc{ownerDiscrepancyCount > 1 ? 'ies' : 'y'}.
                        <SvgIcon icon={warningCircleIcon} themeColor='warning' className="ml-2" size="xlarge" />
                      </h5>}
                      {ownerDiscrepancyCount > 0 && <div style={{ whiteSpace: 'nowrap' }}>
                        <Button svgIcon={searchIcon} themeColor='warning' disabled={loading} onClick={() => openWindowPerc(`/Settlements/OwnerDiscrepancies?payPeriodID=${selectedPayPeriod.PayPeriodID}`, .7, .7, 'ReviewDeductionDiscrepancies')}>Review Discrepancies</Button>
                        <Button svgIcon={arrowRotateCcwIcon} themeColor='secondary' disabled={loading} onClick={() => getOwnerDiscrepancies(selectedPayPeriod)} className="ml-2">Check Again For Discrepancies</Button>
                        <Button svgIcon={arrowRightIcon} onClick={() => {
                          setStep(2);
                          setStoredItems(prev =>
                            prev.map((item, index) =>
                              index === 1 ? { ...item, disabled: false } : item
                            )
                          );
                        }} themeColor='success' disabled={loading} className="ml-2">Ignore And Skip To Next Step</Button>
                      </div>}

                      {ownerDiscrepancyCount === -1 && loading &&
                        <div>
                          <h5>
                            Checking for owner discrepancies...
                            <SvgIcon icon={warningCircleIcon} themeColor='warning' className="ml-2" size="xlarge" />
                          </h5>
                        </div>
                      }

                      {ownerDiscrepancyCount === 0 && !loading &&
                        <div>
                          <h5>
                            No owner discrepancies found
                            <SvgIcon icon={checkIcon} themeColor='success' className="ml-2" size="xlarge" />
                          </h5>
                          <Button svgIcon={arrowRightIcon} onClick={() => {
                            setStep(2);
                            setStoredItems(prev =>
                              prev.map((item, index) =>
                                index === 1 ? { ...item, disabled: false } : item
                              )
                            );
                          }} themeColor='success' disabled={loading}>Next Step</Button>
                        </div>
                      }
                    </div>}

                    {step === 2 && <div>
                      <h5>
                        Pay period {pp} is ready to be posted.
                        <SvgIcon icon={checkIcon} themeColor='success' className="ml-2" size="xlarge" />
                      </h5>
                      <Button themeColor={"primary"}
                        disabled={loading}
                        onClick={async () => {
                          if (!await confirm(`${getPPConfirmationInfo(selectedPayPeriod)}`, 'Confirm'))
                            return;
                          generatePayPostBatch(selectedPayPeriod);
                          closePostPayWindow();
                        }}>
                        {`Post ${selectedPayPeriod.Name} (${selectedPayPeriod.PayPeriodID}) Pay Period`}
                      </Button>
                    </div>}
                  </div>
                  <div className="col-2">
                  </div>
                </div>
              </div>
              <div className="col-1">
              </div>
            </div>

          </div>
          <WindowActionsBar>
            <Button onClick={closePostPayWindow}>Cancel</Button>
          </WindowActionsBar>
        </KendoWindow>
      }
      <h4 className="text-left">Post Pay Batch</h4>
      <Push style={{ zIndex: 0, width: '100%' }}>
        {message && <NotificationGroup style={{ position: "relative", alignItems: "normal", zIndex: 0, width: '100%' }}>
          <Notification style={{ padding: "8px", width: '100%' }} type={{ style: "success", icon: true }}>
            <div>{message}</div>
          </Notification>
        </NotificationGroup>}
      </Push>

      <div className="mb-4">
        <Grid
          data={details?.PayPeriodGroups}
          detail={detailComponent}
          dataItemKey='PayPeriodID'
        >
          <Column field="PayPeriodID" title="Pay Period ID" />
          <Column field="Name" title="Pay Period Name" />
          <Column field="Division" title="Division" cells={{ data: DivisionCell }} filterable={false} />
          <Column field="PeriodEndDate" title="Trips Up To" cells={{ data: DateOnlyCell }} minResizableWidth={160} width={160} />
          <Column field="PaperworkDeadline" title="Paperwork Checked In By" cells={{ data: DateOnlyCell }} minResizableWidth={160} width={160} />
          <Column field="PayDate" title="Pay Date" cells={{ data: DateOnlyCell }} minResizableWidth={160} width={160} />
          <Column field="PayCount" title="Pay Count" cells={{ data: RightAlignCell, headerCell: RightAlignHeaderCell }} minResizableWidth={160} width={160} />
          <Column cells={{ data: MyEditCommandCell }} width={210} filterable={false} />
        </Grid>
      </div>

      <div>
        <Grid
          data={details?.PayGroups}
          detail={detailComponent}
          dataItemKey='UserID'
        >
          <Column field="UserName" title="User ID" />
          <Column field="PayCount" title="Pay Count" cells={{ data: RightAlignCell, headerCell: RightAlignHeaderCell }} />
        </Grid>
      </div>
      <ConfirmationDialog />
    </div >
  );
};

export default PostPayBatch;
